import { cx } from "@emotion/css";
import InputAdornment from "@mui/material/InputAdornment";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import routing from "routing";
import {
	IconButton,
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import Box from "src/components/Default/Main/Box";
import type { FolderItemType } from "src/components/Folders/types";
import {
	Modal,
	ModalFooter,
	ModalTitle,
} from "src/components/Modals/Components/ModalsComponents";
import isMobile from "src/helpers/isMobile";
import { useDebounce } from "src/hooks/useDebounce";
import { useAddFolderItems } from "src/hooks/useFolders";
import { useHideOnModalScroll } from "src/hooks/useHideOnModalScroll";
import FolderIcon from "src/images/Icons/newIcons/folder.svg";
import SearchIcon from "src/images/Icons/newIcons/search.svg";
import { ModalMaxWidth } from "src/types/enums";
import { ScrollShadowWrapper } from "../Default/LekUI/ScrollShadowWrapper";
import { CoursesFolderTab } from "./AddFolderContentTabs/CoursesFolderTab";
import { ExamsFolderTab } from "./AddFolderContentTabs/ExamsFolderTab";
import { FlashcardsFolderTab } from "./AddFolderContentTabs/FlashcardsFolderTab";
import { NotesFolderTab } from "./AddFolderContentTabs/NotesFolderTab";

interface ModalSelectedItem {
	itemId: string;
	itemType: FolderItemType;
}

interface TabItem {
	id: string;
	[key: string]: unknown;
}

interface AddFolderContentModalProps {
	onClose: () => void;
	folderId: string;
	folderName?: string;
	initialTab?: FolderItemType;
}

export const AddFolderContentModal = ({
	onClose,
	folderId,
	folderName,
	initialTab,
}: AddFolderContentModalProps) => {
	const { t } = useTranslation();
	const history = useHistory();

	const TAB_TYPES: FolderItemType[] = ["exam", "course", "deck", "note"];
	const initialTabIndex = initialTab ? TAB_TYPES.indexOf(initialTab) : 0;

	const [selectedTab, setSelectedTab] = useState(
		initialTabIndex !== -1 ? initialTabIndex : 0,
	);

	const [selectedItems, setSelectedItems] = useState<ModalSelectedItem[]>([]);
	const [searchValue, setSearchValue] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const debouncedSearchValue = useDebounce(searchValue, 500);
	const { anchorRef, transformClass } = useHideOnModalScroll({
		containerSelector: isMobile()
			? ".modal--content"
			: ".modal--scrollable-list",
	});

	const activeType = TAB_TYPES[selectedTab];

	const { mutateAsync: addFolderItems, isPending: isAdding } =
		useAddFolderItems();

	const CREATE_CONFIG: Record<FolderItemType, { text: string; path: string }> =
		{
			exam: {
				text: t("folder.newExam", "Nowy egzamin"),
				path: `${routing.paths.pickExamType}?folderId=${folderId}`,
			},
			course: {
				text: t("folder.newCourse", "Nowy kurs"),
				path: `${routing.paths.pickCourseType}?folderId=${folderId}`,
			},
			deck: {
				text: t("folder.newDeck", "Nowy zestaw"),
				path: `${routing.paths.pickFlashcardsDeckChoice}?folderId=${folderId}`,
			},
			note: {
				text: t("folder.newNote", "Nowa notatka"),
				path: (
					routing.getUrl as (
						url: string,
						params: Record<string, string>,
					) => string
				)(routing.paths.createNote, { id: folderId }),
			},
		};

	const handleCreate = () => {
		onClose();
		history.push(CREATE_CONFIG[activeType].path);
	};

	const handleTabChange = (val: number) => {
		setSelectedTab(val);
		setSearchValue("");
	};

	const tabs = [
		t("folder.tabs.exams", "Egzaminy"),
		t("folder.tabs.courses", "Kursy"),
		t("folder.tabs.flashcards", "Fiszki"),
		t("folder.tabs.notes", "Notatki"),
	];

	const toggleSelection = (item: TabItem, type: FolderItemType) => {
		setSelectedItems((prev) => {
			const exists = prev.find(
				(i) => i.itemId === item.id && i.itemType === type,
			);
			if (exists) {
				return prev.filter(
					(i) => !(i.itemId === item.id && i.itemType === type),
				);
			}
			return [...prev, { itemId: item.id as string, itemType: type }];
		});
	};

	const clearSelection = () => {
		setSelectedItems([]);
	};

	const handleSave = async () => {
		if (selectedItems.length > 0) {
			await addFolderItems({ folderId, items: selectedItems });
		}
		onClose();
	};

	const renderContent = () => {
		const tabProps = {
			debouncedSearchValue,
			selectedItems,
			toggleSelection,
			onClose,
			setIsSearching,
			folderId,
		};

		if (activeType === "exam") return <ExamsFolderTab {...tabProps} />;
		if (activeType === "course") return <CoursesFolderTab {...tabProps} />;
		if (activeType === "deck") return <FlashcardsFolderTab {...tabProps} />;
		return <NotesFolderTab {...tabProps} />;
	};

	return (
		<Modal
			onClose={onClose}
			maxWidth={ModalMaxWidth.lg}
			className="add-folder-content-modal"
		>
			<ModalTitle
				title={t(
					"folder.selectContentToFolder",
					"Wybierz treści do folderu {{folderName}}",
					{ folderName: folderName || "" },
				)}
				Icon={FolderIcon}
				headerClassName="text-ellipsis-1"
			/>
			<div className="flex flex-col w-100 modal--scrollable-list">
				<Box
					tabsClassName="basic-box--table-tabs folders-items-list-header"
					TabsWrapper={ScrollShadowWrapper}
					className="border-none box-shadow-none p-0"
					headerClassName="mb-0 d-none"
					tabs={tabs}
					selectedTab={selectedTab}
					onTabsChange={handleTabChange}
					tabTestIds={[
						"folder-content-tab-exams",
						"folder-content-tab-courses",
						"folder-content-tab-flashcards",
						"folder-content-tab-notes",
					]}
					additionalTabsActions={() => (
						<div className="add-folder-content-actions">
							<div className="flex items-center gap-8">
								<span className="font-14 text-black-80">
									{t("folder.selectedCount", "Wybrano: {{count}}", {
										count: selectedItems.length,
									})}
								</span>
								{selectedItems.length > 0 && (
									<IconButton onClick={clearSelection}>
										<X className="icon-super-small" />
									</IconButton>
								)}
							</div>
							<PrimaryButton
								onClick={handleCreate}
								className="flex items-center gap-4 w-fit-content add-folder-content-create-btn"
								data-test-id="folder-create-new-button"
							>
								<Plus className="icon-small add-folder-content-create-icon" />
								<span className="add-folder-content-create-text">
									{CREATE_CONFIG[activeType].text}
								</span>
							</PrimaryButton>
						</div>
					)}
				>
					<div
						ref={anchorRef}
						className={cx("modal-scroll-hide-header", transformClass)}
					>
						<TextFieldGrey
							variant="filled"
							placeholder={t("folder.search", "Szukaj")}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
							className="w-100"
							autoFocus={!isMobile()}
							loading={isSearching}
							slotProps={{
								input: {
									disableUnderline: true,
									startAdornment: (
										<InputAdornment position="start" className="m-0 mr-8">
											<SearchIcon className="icon-normal icon-black-80" />
										</InputAdornment>
									),
								},
							}}
						/>
					</div>
					{renderContent()}
				</Box>
			</div>
			<ModalFooter>
				<TextPrimaryButton onClick={onClose} disabled={isAdding}>
					{t("commands.cancel", "Anuluj")}
				</TextPrimaryButton>
				<PrimaryButton
					onClick={handleSave}
					disabled={isAdding || selectedItems.length === 0}
					data-test-id="folder-add-content-save-button"
				>
					{t("commands.saveChanges", "Zapisz zmiany")}
				</PrimaryButton>
			</ModalFooter>
		</Modal>
	);
};
